import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getApiBase, getToken } from '../lib/utils'
import WhatsAppStatsWidget from '../components/WhatsAppStatsWidget'
import { useWebSocket } from '../hooks/useWebSocket'
import toast, { Toaster } from 'react-hot-toast'

// Helper pour formater date relative
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMins < 1) return 'à l\'instant'
  if (diffMins < 60) return `il y a ${diffMins} min`
  if (diffHours < 24) return `il y a ${diffHours}h`
  if (diffDays < 7) return `il y a ${diffDays}j`
  return date.toLocaleDateString('fr-FR')
}

// Helper pour obtenir le badge de source
function getSourceBadge(source: string) {
  switch (source) {
    case 'whatsapp':
      return {
        label: 'WhatsApp',
        icon: '💬',
        classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      }
    case 'widget_external':
      return {
        label: 'Widget externe',
        icon: '🔌',
        classes: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
      }
    case 'widget_internal':
    default:
      return {
        label: 'Widget interne',
        icon: '🌐',
        classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      }
  }
}

interface Conversation {
  id: number
  user_id: number
  agent_id: number
  agent_name: string
  title: string
  is_favorite: boolean
  source: 'widget_internal' | 'widget_external' | 'whatsapp'
  message_count: number
  human_handoff: boolean
  created_at: string
  updated_at: string
}

interface Agent {
  id: number
  name: string
}

interface User {
  id: number
  email: string
  first_name?: string
  last_name?: string
}

interface Message {
  id: number
  conversation_id: number
  role: string
  content: string
  tool_calls: any
  tool_call_id: string | null
  tool_name: string | null
  created_at: string
}

export default function ConversationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // États filtres (initialisés depuis URL)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(
    searchParams.get('agent_id') ? Number(searchParams.get('agent_id')) : null
  )
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedSource, setSelectedSource] = useState<'all' | 'whatsapp' | 'widget_external' | 'widget_internal'>(
    (searchParams.get('source') as 'all' | 'whatsapp' | 'widget_external' | 'widget_internal') || 'all'
  )
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [messageFilter, setMessageFilter] = useState<'all' | 'short' | 'medium' | 'long'>('all')
  const [periodFilter, setPeriodFilter] = useState<'all' | 'today' | 'week' | 'month'>('all')
  
  // Listes pour dropdowns
  const [agents, setAgents] = useState<Agent[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [selectedAgentIds, setSelectedAgentIds] = useState<number[]>([])
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState<'all' | 'favorites'>('all')
  const [allAgents, setAllAgents] = useState<Agent[]>([])
  const [showFilters, setShowFilters] = useState(false)
  
  // Phase 1: WebSocket temps réel
  const { isConnected, subscribe } = useWebSocket()

  // Calculer le prénom
  const firstName = useMemo(() => {
    if (currentUser?.first_name) return currentUser.first_name
    if (!currentUser?.email) return 'Profil'
    const local = String(currentUser.email).split('@')[0]
    const parts = local.replace(/\d+/g, '').split(/[._-]+/).filter(Boolean)
    const name = (parts[0] || local)
    return name.charAt(0).toUpperCase() + name.slice(1)
  }, [currentUser])

  // Charger user actuel
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      setCurrentUser(user)
    } catch {}
  }, [])

  // Charger agents et users une seule fois
  useEffect(() => {
    loadAgents()
    if (currentUser?.is_superuser) {
      console.log('Loading users (admin mode)')
      loadUsers()
    } else {
      console.log('Not admin, skipping users load', currentUser)
    }
  }, [currentUser])

  // Charger conversations quand filtres changent
  useEffect(() => {
    if (currentUser) {
      loadConversations()
    }
  }, [searchTerm, selectedAgentId, selectedUserId, selectedSource, favoritesOnly, sortBy, sortOrder, messageFilter, periodFilter, currentUser])
  
  // Phase 1: Écouter événements WebSocket temps réel
  // Fetch UNE seule conversation (optimisé)
  const fetchSingleConversation = async (conversationId: number) => {
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations?limit=100&sort_by=updated_at&sort_order=desc`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) return
      
      const data = await response.json()
      const updatedConv = data.items.find((c: Conversation) => c.id === conversationId)
      
      if (updatedConv) {
        updateConversationIncremental(conversationId, updatedConv)
      }
    } catch (err) {
      console.error('Erreur fetch conversation:', err)
    }
  }

  // Mise à jour incrémentale d'une conversation spécifique (sans recharger toute la liste)
  const updateConversationIncremental = (conversationId: number, updates: Partial<Conversation>) => {
    setConversations(prevConvs => {
      const existingIndex = prevConvs.findIndex(c => c.id === conversationId)
      
      if (existingIndex >= 0) {
        // Mettre à jour la conversation existante avec les nouvelles données
        const newConvs = [...prevConvs]
        newConvs[existingIndex] = {
          ...newConvs[existingIndex],
          ...updates
        }
        
        // Trier par updated_at si nécessaire (pour remonter en haut)
        if (sortBy === 'updated_at') {
          return newConvs.sort((a, b) => {
            const dateA = new Date(a.updated_at).getTime()
            const dateB = new Date(b.updated_at).getTime()
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
          })
        }
        
        return newConvs
      }
      
      return prevConvs
    })
  }

  useEffect(() => {
    if (!subscribe) return
    
    // Écouter conversation_update (nouveau message, changement statut)
    const unsubscribeConv = subscribe('conversation_update', (data) => {
      console.log('🔄 Rafraîchissement auto conversation:', data.conversation_id)
      console.log('📊 Données événement:', JSON.stringify(data, null, 2))
      
      // Toast notification EN PREMIER
      if (data.action === 'message_added') {
        toast('💬 Nouveau message', {
          duration: 3000,
          icon: '📨',
        })
      }
      
      // Mise à jour incrémentale de la conversation (sans recharger toute la page)
      // Utiliser les données de l'événement WebSocket directement (optimisé)
      if (data.data?.message_count !== undefined && data.data?.updated_at) {
        console.log('✅ Mise à jour incrémentale:', {
          conversation_id: data.conversation_id,
          message_count: data.data.message_count,
          updated_at: data.data.updated_at
        })
        updateConversationIncremental(data.conversation_id, {
          message_count: data.data.message_count,
          updated_at: data.data.updated_at
        })
      } else {
        console.warn('⚠️ Données manquantes, fallback sur fetch API')
        // Fallback : faire un fetch pour cette conversation spécifique
        fetchSingleConversation(data.conversation_id)
      }
      
      // Si la conversation est sélectionnée, rafraîchir les messages
      if (selectedConversation && selectedConversation.id === data.conversation_id) {
        loadMessages(data.conversation_id)
      }
    })
    
    // Écouter takeover_event (takeover/release manuel ou auto)
    const unsubscribeTakeover = subscribe('takeover_event', (data) => {
      console.log('👤 Événement takeover:', data.event_type)
      
      // Rafraîchir la liste
      loadConversations()
      
      // Toast selon le type
      if (data.event_type === 'takeover') {
        toast.success('👤 Takeover activé', {
          duration: 3000,
        })
      } else if (data.event_type === 'release') {
        toast.success('🤖 Agent réactivé', {
          duration: 3000,
        })
      } else if (data.event_type === 'auto_takeover') {
        toast('🤖→👤 Auto-takeover détecté', {
          duration: 4000,
          icon: '🔔',
        })
      }
    })
    
    // Écouter notification (système)
    const unsubscribeNotif = subscribe('notification', (data) => {
      const icon = data.type === 'error' ? '❌' : data.type === 'warning' ? '⚠️' : data.type === 'success' ? '✅' : 'ℹ️'
      
      if (data.type === 'error') {
        toast.error(data.message, { duration: 5000, icon })
      } else if (data.type === 'warning') {
        toast(data.message, { duration: 4000, icon })
      } else if (data.type === 'success') {
        toast.success(data.message, { duration: 3000, icon })
      } else {
        toast(data.message, { duration: 3000, icon })
      }
    })
    
    // Cleanup
    return () => {
      unsubscribeConv()
      unsubscribeTakeover()
      unsubscribeNotif()
    }
  }, [subscribe, selectedConversation])

  const loadConversations = async () => {
    try {
      setLoading(true)
      const token = getToken()
      
      // Construire query params
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (selectedAgentId) params.append('agent_id', String(selectedAgentId))
      if (selectedUserId && currentUser?.is_superuser) params.append('user_id', String(selectedUserId))
      if (selectedSource !== 'all') params.append('source', selectedSource)
      if (favoritesOnly) params.append('favorites_only', 'true')
      params.append('sort_by', sortBy)
      params.append('sort_order', sortOrder)
      
      // Filtre période
      if (periodFilter !== 'all') {
        const now = new Date()
        let dateFrom = new Date()
        if (periodFilter === 'today') {
          dateFrom.setHours(0, 0, 0, 0)
        } else if (periodFilter === 'week') {
          dateFrom.setDate(now.getDate() - 7)
        } else if (periodFilter === 'month') {
          dateFrom.setMonth(now.getMonth() - 1)
        }
        params.append('date_from', dateFrom.toISOString())
      }
      
      // Filtre messages
      if (messageFilter === 'short') {
        params.append('max_messages', '3')
      } else if (messageFilter === 'medium') {
        params.append('min_messages', '4')
        params.append('max_messages', '10')
      } else if (messageFilter === 'long') {
        params.append('min_messages', '11')
      }
      
      const response = await fetch(`${getApiBase()}/conversations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des conversations')
      }

      const data = await response.json()
      console.log('Conversations loaded (full):', JSON.stringify(data.items?.slice(0, 1), null, 2))
      setConversations(data.items || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }
  
  const loadAgents = async () => {
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setAgents(data.items || [])
      }
    } catch {}
  }
  
  const loadUsers = async () => {
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/auth/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        console.log('Users loaded:', data)
        setUsers(Array.isArray(data) ? data : (data.items || []))
      } else {
        console.error('Failed to load users:', response.status)
      }
    } catch (err) {
      console.error('Error loading users:', err)
    }
  }

  // Charger messages d'une conversation
  const loadMessages = async (conversationId: number) => {
    try {
      setMessagesLoading(true)
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des messages')
      }

      const data = await response.json()
      setMessages(data.messages || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setMessagesLoading(false)
    }
  }

  // Sélectionner conversation
  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv)
    loadMessages(conv.id)
  }

  // Supprimer conversation
  const handleDeleteConversation = async (conversationId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette conversation ?')) {
      return
    }

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression')
      }

      // Recharger liste
      loadConversations()
      
      // Désélectionner si c'était la conversation active
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
        setMessages([])
      }
    } catch (err: any) {
      alert(`Erreur: ${err.message}`)
    }
  }

  // Toggle favori
  const handleToggleFavorite = async (conversationId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations/${conversationId}/favorite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Mettre à jour localement
        setConversations(conversations.map(conv =>
          conv.id === conversationId
            ? { ...conv, is_favorite: !conv.is_favorite }
            : conv
        ))
        
        // Mettre à jour la conversation sélectionnée si c'est celle-ci
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation({
            ...selectedConversation,
            is_favorite: !selectedConversation.is_favorite
          })
        }
      }
    } catch (err) {
      console.error('Erreur toggle favorite:', err)
    }
  }

  // Reprendre conversation dans ChatWidget
  const handleResumeConversation = (conversationId: number) => {
    try {
      localStorage.setItem('chat_conversation_id', String(conversationId))
      alert('Conversation chargée ! Ouvrez le ChatWidget pour continuer.')
    } catch (err) {
      alert('Erreur lors du chargement de la conversation')
    }
  }

  // Prendre le contrôle d'une conversation (Human Takeover)
  const handleTakeover = async (conversationId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/conversations/${conversationId}/takeover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Recharger pour mettre à jour le statut
        await loadConversations()
        // Si c'est la conversation sélectionnée, recharger aussi
        if (selectedConversation?.id === conversationId) {
          const updated = conversations.find(c => c.id === conversationId)
          if (updated) setSelectedConversation(updated)
        }
      } else {
        const data = await response.json()
        alert(`Erreur: ${data.detail || 'Impossible de prendre le contrôle'}`)
      }
    } catch (err) {
      console.error('Erreur takeover:', err)
      alert('Erreur lors de la prise de contrôle')
    }
  }

  // Relâcher le contrôle d'une conversation
  const handleRelease = async (conversationId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/conversations/${conversationId}/release`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Recharger pour mettre à jour le statut
        await loadConversations()
        // Si c'est la conversation sélectionnée, recharger aussi
        if (selectedConversation?.id === conversationId) {
          const updated = conversations.find(c => c.id === conversationId)
          if (updated) setSelectedConversation(updated)
        }
      } else {
        const data = await response.json()
        alert(`Erreur: ${data.detail || 'Impossible de relâcher le contrôle'}`)
      }
    } catch (err) {
      console.error('Erreur release:', err)
      alert('Erreur lors du relâchement du contrôle')
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">Chargement des conversations...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700">
          Erreur: {error}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="relative min-h-screen overflow-hidden">
        {/* Gradient de fond (comme AgentsPage) */}
        <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
        <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
        
        {/* Container principal avec padding comme AgentsPage */}
        <div className="mx-auto max-w-[1600px] px-6 pt-6 pb-12">
          <div className="flex h-[calc(100vh-8rem)] rounded-3xl border border-emerald-100/70 bg-white/90 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90 overflow-hidden">
        {/* Liste conversations (sidebar) */}
        <div className="w-1/3 bg-white/50 dark:bg-zinc-900/50 border-r border-emerald-100/70 dark:border-emerald-900/30 overflow-y-auto">
        <div className="p-4 border-b border-emerald-100/70 dark:border-emerald-900/30 bg-emerald-50/30 dark:bg-emerald-950/20">
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1.5 text-sm font-semibold bg-white dark:bg-zinc-700 border border-emerald-200/70 dark:border-emerald-900/50 rounded-full hover:bg-emerald-50 dark:hover:bg-zinc-600 transition text-emerald-700 dark:text-emerald-300"
            >
              {showFilters ? '✕ Masquer' : '🔍 Filtres'}
            </button>
          </div>
          
          {/* Onglets source rapides */}
          <div className="flex gap-2 mb-3 overflow-x-auto">
            <button
              onClick={() => setSelectedSource('all')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition ${
                selectedSource === 'all'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-white dark:bg-zinc-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setSelectedSource('whatsapp')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition ${
                selectedSource === 'whatsapp'
                  ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                  : 'bg-white dark:bg-zinc-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600'
              }`}
            >
              💬 WhatsApp
            </button>
            <button
              onClick={() => setSelectedSource('widget_external')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition ${
                selectedSource === 'widget_external'
                  ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-white dark:bg-zinc-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600'
              }`}
            >
              🔌 Widget Ext
            </button>
            <button
              onClick={() => setSelectedSource('widget_internal')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition ${
                selectedSource === 'widget_internal'
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white dark:bg-zinc-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600'
              }`}
            >
              🌐 Widget Int
            </button>
          </div>
          
          {/* Badge filtre actif */}
          {(selectedAgentId || selectedSource !== 'all') && (
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">📌 Filtré par :</span>
              {selectedAgentId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                  Agent #{selectedAgentId}
                  <button onClick={() => setSelectedAgentId(null)} className="hover:text-blue-900 dark:hover:text-blue-100">✕</button>
                </span>
              )}
              {selectedSource !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium rounded-full">
                  {selectedSource === 'whatsapp' ? '💬 WhatsApp' : selectedSource === 'widget_external' ? '🔌 Widget Ext' : '🌐 Widget Int'}
                  <button onClick={() => setSelectedSource('all')} className="hover:text-green-900 dark:hover:text-green-100">✕</button>
                </span>
              )}
              <button
                onClick={() => {
                  setSelectedAgentId(null)
                  setSelectedSource('all')
                }}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
              >
                Tout effacer
              </button>
            </div>
          )}
          
          {/* Phase 1: Widget Stats WhatsApp (uniquement si filtre WhatsApp actif) */}
          {selectedSource === 'whatsapp' && (
            <div className="mb-3">
              <WhatsAppStatsWidget />
            </div>
          )}
          
          {/* Indicateur WebSocket */}
          <div className="mb-2 flex items-center gap-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">{conversations.length} conversation(s)</p>
            {isConnected && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Temps réel
              </span>
            )}
          </div>
        </div>

        {/* Barre de filtres */}
        {showFilters && (
          <div className="p-4 border-b border-emerald-100/70 dark:border-emerald-900/30 bg-emerald-50/20 dark:bg-emerald-950/10 space-y-3">
            {/* Recherche */}
            <input
              type="text"
              placeholder="🔍 Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            />

            {/* Agent */}
            <select
              value={selectedAgentId || ''}
              onChange={(e) => setSelectedAgentId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Tous les agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>

            {/* Utilisateur (admin only) */}
            {currentUser?.is_superuser && (
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.email} {user.first_name && `(${user.first_name})`}
                  </option>
                ))}
              </select>
            )}

            {/* Période */}
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Toute période</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
            </select>

            {/* Nombre de messages */}
            <select
              value={messageFilter}
              onChange={(e) => setMessageFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Tous les messages</option>
              <option value="short">Courtes (1-3 msg)</option>
              <option value="medium">Moyennes (4-10 msg)</option>
              <option value="long">Longues (10+ msg)</option>
            </select>

            {/* Tri */}
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="updated_at">Dernière activité</option>
                <option value="created_at">Date création</option>
                <option value="title">Titre</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-600 transition"
                title={sortOrder === 'desc' ? 'Décroissant' : 'Croissant'}
              >
                {sortOrder === 'desc' ? '↓' : '↑'}
              </button>
            </div>

            {/* Favoris */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={favoritesOnly}
                onChange={(e) => setFavoritesOnly(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-zinc-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">⭐ Favoris uniquement</span>
            </label>

            {/* Reset */}
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedAgentId(null)
                setSelectedUserId(null)
                setFavoritesOnly(false)
                setSortBy('updated_at')
                setSortOrder('desc')
                setMessageFilter('all')
                setPeriodFilter('all')
              }}
              className="w-full px-3 py-2 text-sm bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-zinc-600 transition"
            >
              ✕ Réinitialiser les filtres
            </button>
          </div>
        )}

        <div className="divide-y divide-emerald-100/50 dark:divide-emerald-900/20">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Aucune conversation
            </div>
          ) : (
            conversations.map((conv) => {
              // Debug: vérifier le type de is_favorite
              if (conv.id === 1) {
                console.log('Conv 1 - is_favorite:', conv.is_favorite, 'type:', typeof conv.is_favorite)
              }
              return (
              <div
                key={conv.id}
                className={`p-4 cursor-pointer hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 transition ${
                  selectedConversation?.id === conv.id ? 'bg-emerald-100/60 dark:bg-emerald-900/30 border-l-4 border-emerald-500' : ''
                }`}
                onClick={() => handleSelectConversation(conv)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {conv.title}
                      </h3>
                      {conv.is_favorite && <span className="text-yellow-500">⭐</span>}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Agent: {conv.agent_name}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                      {/* Badge source */}
                      {(() => {
                        const badge = getSourceBadge(conv.source)
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                            <span>{badge.icon}</span>
                            <span>{badge.label}</span>
                          </span>
                        )
                      })()}
                      {/* Badge Human Takeover */}
                      {conv.source === 'whatsapp' && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          conv.human_handoff
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        }`}>
                          <span>{conv.human_handoff ? '👤' : '🤖'}</span>
                          <span>{conv.human_handoff ? 'Humain' : 'Auto'}</span>
                        </span>
                      )}
                      <span className="text-gray-400 dark:text-gray-500">{conv.message_count} message(s)</span>
                      <span className="text-gray-400 dark:text-gray-500">•</span>
                      <span className="text-gray-400 dark:text-gray-500">
                        {formatRelativeTime(conv.updated_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Boutons Takeover/Release (WhatsApp uniquement) */}
                    {conv.source === 'whatsapp' && (
                      conv.human_handoff ? (
                        <button
                          onClick={(e) => handleRelease(conv.id, e)}
                          className="p-1.5 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition"
                          title="Relâcher le contrôle (réactiver l'agent)"
                        >
                          <span className="text-lg">🤖</span>
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleTakeover(conv.id, e)}
                          className="p-1.5 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition"
                          title="Prendre le contrôle (désactiver l'agent)"
                        >
                          <span className="text-lg">👤</span>
                        </button>
                      )
                    )}
                    <button
                      onClick={(e) => handleToggleFavorite(conv.id, e)}
                      className={`p-1 transition ${conv.is_favorite ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-400 hover:text-yellow-500'}`}
                      title={conv.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    >
                      <span className="text-xl">{conv.is_favorite ? '⭐' : '☆'}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteConversation(conv.id)
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 transition"
                      title="Supprimer"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              )
            })
          )}
        </div>
      </div>

      {/* Messages (main) */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-emerald-100/70 dark:border-emerald-900/30 bg-emerald-50/30 dark:bg-emerald-950/20 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedConversation.title}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Agent: {selectedConversation.agent_name} • {selectedConversation.message_count} message(s)
                </p>
              </div>
              <button
                onClick={() => handleResumeConversation(selectedConversation.id)}
                className="px-4 py-2 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-600 transition flex items-center gap-2 shadow-lg shadow-emerald-500/30"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Reprendre
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-white/50 to-emerald-50/30 dark:from-zinc-900/50 dark:to-zinc-950/50">
              {messagesLoading ? (
                <div className="text-center text-gray-500 dark:text-gray-400">Chargement des messages...</div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400">Aucun message</div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-2xl rounded-2xl p-4 ${
                        msg.role === 'user'
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                          : msg.role === 'assistant'
                          ? 'bg-white/90 dark:bg-zinc-800/90 border border-emerald-100/70 dark:border-emerald-900/30 shadow-md'
                          : 'bg-gray-100 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700'
                      }`}
                    >
                      {/* Role badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            msg.role === 'user'
                              ? 'bg-emerald-400 text-white'
                              : msg.role === 'assistant'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {msg.role === 'user' ? 'Vous' : msg.role === 'assistant' ? 'Assistant' : msg.role}
                        </span>
                        <span className={`text-xs ${msg.role === 'user' ? 'text-emerald-100' : 'text-gray-400'}`}>
                          {formatRelativeTime(msg.created_at)}
                        </span>
                      </div>

                      {/* Content */}
                      <div className={`whitespace-pre-wrap ${msg.role === 'user' ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        {msg.content || <em className="text-gray-400 dark:text-gray-500">Pas de contenu</em>}
                      </div>

                      {/* Tool calls */}
                      {msg.tool_calls && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-zinc-700">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Outils appelés:</div>
                          <pre className="text-xs bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-gray-100 p-2 rounded overflow-x-auto">
                            {JSON.stringify(msg.tool_calls, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Tool result */}
                      {msg.tool_name && (
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          🔧 Résultat de: {msg.tool_name}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-white/50 to-emerald-50/30 dark:from-zinc-900/50 dark:to-zinc-950/50">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                <svg className="w-12 h-12 text-emerald-500 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">Sélectionnez une conversation</p>
              <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">pour voir l'historique des messages</p>
            </div>
          </div>
        )}
      </div>
          </div>
        </div>
      </div>
      
      {/* Phase 1: Toast notifications temps réel */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-text)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  )
}
